import React, { useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import MUIRichTextEditor from 'mui-rte'
import { convertToRaw } from 'draft-js'
import { draftToMarkdown, markdownToDraft } from 'markdown-draft-js'
import { makeStyles } from '@material-ui/core/styles'
import FormSubheader from '../create-profile-form/form-subheader'
import { useStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'
import {
  getFieldProps,
  useCreateProfileFieldStyles,
} from '../create-profile-form/field-styles'

const BIO_CHARACTER_LIMIT = 5000

const useBioStyles = makeStyles(theme => ({
  bioEditorRoot: {
    position: 'relative',
    paddingBottom: theme.spacing(3),
  },
  counterText: {
    position: 'absolute',
    right: theme.spacing(2),
    bottom: theme.spacing(1),
    fontSize: '0.75rem',
    color: props =>
      props.isError ? theme.palette.error.main : theme.palette.text.secondary,
  },
  bioError: {
    marginTop: theme.spacing(0.5),
  },
}))

const BioEditor = ({ editorRootClassName, editorClassName }) => {
  const { register, setValue, getValues } = useFormContext()
  const [charCount, setCharCount] = useState(
    () => getValues('bio')?.length || 0,
  )
  const [isOverLimit, setIsOverLimit] = useState(false)
  const bioClasses = useBioStyles({ isError: isOverLimit })
  const initialBioValueRef = useRef(
    getValues('bio')
      ? JSON.stringify(markdownToDraft(getValues('bio')))
      : undefined,
  )

  useEffect(() => {
    register('bio')
  }, [register])

  return (
    <div className={`${editorRootClassName} ${bioClasses.bioEditorRoot}`}>
      <MUIRichTextEditor
        className={editorClassName}
        label="Tell students about yourself..."
        controls={['title', 'bold', 'italic', 'bulletList', 'link']}
        defaultValue={initialBioValueRef.current}
        onChange={value => {
          const markdown = draftToMarkdown(
            convertToRaw(value.getCurrentContent()),
            {},
          )

          setCharCount(markdown.length)
          setIsOverLimit(markdown.length > BIO_CHARACTER_LIMIT)
          setValue('bio', markdown, {
            shouldDirty: true,
            shouldValidate: false,
          })
        }}
      />
      <Typography className={bioClasses.counterText}>
        {`${charCount}/${BIO_CHARACTER_LIMIT}`}
      </Typography>
      {isOverLimit ? (
        <Typography
          color="error"
          variant="caption"
          className={bioClasses.bioError}
        >
          {`Bio must be ${BIO_CHARACTER_LIMIT} characters or fewer.`}
        </Typography>
      ) : null}
    </div>
  )
}

export const Personal = () => {
  const classes = useStyles()
  const fieldClasses = useCreateProfileFieldStyles()
  const fieldProps = getFieldProps(fieldClasses)
  const { register, errors } = useFormContext()
  const bioError = errors.bio?.message

  return (
    <Box className={fieldClasses.sectionStack}>
      <Box className={fieldClasses.fieldSection}>
        <FormSubheader
          title="Bio"
          description="Use your bio to introduce yourself, your golfing journey, and why students should choose you as their coach. Show your unique personality."
        />
        <BioEditor
          editorRootClassName={classes.editorRoot}
          editorClassName={classes.editor}
        />
        {bioError ? (
          <Typography color="error" variant="caption">
            {bioError}
          </Typography>
        ) : null}
      </Box>
      <Box className={fieldClasses.fieldSection}>
        <FormSubheader title="At a glance" />
        <Box className={fieldClasses.fieldStack}>
          <TextField
            {...fieldProps}
            name="numberOfStudents"
            label="Number of Students"
            type="number"
            inputRef={register()}
            error={!!errors.numberOfStudents}
            inputProps={{
              pattern: '/d+',
            }}
          />
          <TextField
            {...fieldProps}
            name="numberOfFollowers"
            label="Number of Social Media Followers"
            type="number"
            inputRef={register()}
            error={!!errors.numberOfFollowers}
            inputProps={{
              pattern: '/d+',
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default Personal
